import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, qs } from '../lib/api.js';
import { useAction } from '../lib/hooks.js';
import { ErrorNote, Pill, Spinner, announce } from './ui.jsx';

/**
 * Why the pantry check did not run, in the one sentence that is true of this
 * page. "Not checked" on its own reads as a failure of ours, and sends people
 * back to press the same button again.
 */
const UNCHECKED_REASON = {
  collection: 'A roundup page — open it and import the recipe you pick.',
  'fetch-blocked': 'This site refuses automated readers, so the ingredient check could not run.',
  'no-recipe-markup': 'Couldn’t read this page’s ingredient list — open it or add it to find out.',
};

/**
 * A roundup that opened up into nothing, in the sentence that says what to do
 * instead. Both are the page being what it is rather than anything going wrong,
 * so neither is phrased as a failure and neither is worth pressing again.
 */
const NOTHING_FOUND = {
  'no-links': 'This list keeps its recipes on the page itself, so opening it is the way in.',
  // A different fact entirely, and saying the first one here would be untrue:
  // the list does link its recipes, at sites nobody has added. Naming where
  // that is changed turns it into something the reader can act on.
  'off-list': 'Every recipe on this list is on a site you haven’t added. '
    + 'Add it under Settings, Recipe sources — or open the list as it is.',
  'single-recipe': 'This page is one recipe after all, so adding it is the way in.',
};

/**
 * The same, for a reason this client has never heard of.
 *
 * A server that learns a new one must not make the button and the sentence
 * vanish here: the card would sit there having visibly done nothing, with
 * nothing on it to read. Said once, so what is announced and what is rendered
 * cannot disagree about what an unrecognised outcome means.
 */
const nothingFound = (reason) => NOTHING_FOUND[reason] || 'No recipes came out of that list.';

/**
 * The publisher's own choice about robots, said as such. AllRecipes, Serious
 * Eats and Southern Living all answer this way, so it is a routine outcome
 * rather than a rare one, and it must not read like a broken app.
 */
const refusedNote = (sourceName) => `${sourceName} refuses automated readers, `
  + 'so its recipes can only be reached by opening the list.';

/**
 * One search result, and the several ways adding it can turn out.
 *
 * Lifted out of DiscoverRecipes because Cook Now searches the same sources with
 * the same result shape: a hit found by ingredient and a hit found by name are
 * the same card, and two copies of the import flow would have drifted the first
 * time one of them learned a new failure mode.
 *
 * The pantry fields -- summary, checked, already_saved -- are additive. A plain
 * text search sends none of them and renders exactly what it always did.
 *
 * A roundup can be opened up into the recipes it names, which arrive as hits of
 * this same shape and render as more of these cards. `canExpand` is how that
 * stops after one level: the children are told no, so a roundup that lists
 * another roundup offers it to be opened rather than unpacked again -- which is
 * both a page fetch per card and a list nobody asked to see.
 */
export default function SearchHit({ hit, onImported, canExpand = true }) {
  const { busy, error, run } = useAction();
  const expanding = useAction();
  const [blocked, setBlocked] = useState(false);
  const [collection, setCollection] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [expansion, setExpansion] = useState(null);
  const [showing, setShowing] = useState(false);

  const add = () => run(async () => {
    setBlocked(false);
    setCollection(null);
    setDuplicate(null);
    try {
      const recipe = await api.post('/recipes/import/url', { url: hit.url });
      onImported(recipe);
    } catch (err) {
      // 409 carries the id of the recipe already holding this URL, so the
      // useful response is a way to open it rather than an error to read.
      if (err.status === 409 && err.data?.recipeId) {
        setDuplicate(err.data.recipeId);
        return;
      }
      // Two different things arrive as 422 and they need different advice, so
      // the code is checked before the status: pasting is the answer to a
      // publisher blocking us, and is no help at all on a roundup.
      if (err.data?.code === 'RECIPE_IS_COLLECTION') {
        // A hit nobody labelled a collection can still be one, and the page
        // says so on the way past. `expandable` means it published enough
        // recipe links to be worth opening up here; without it the refusal is
        // all there is to say.
        setCollection({ message: err.message, expandable: Boolean(err.data.expandable) });
        return;
      }
      if (err.status === 422) {
        setBlocked(true);
        return;
      }
      throw err;
    }
  });

  // Announced as well as rendered. Every ending here is a card that either
  // quietly grew a dozen children or quietly grew none, and neither reaches
  // somebody who is not watching the screen.
  const fetchChildren = () => expanding.run(async () => {
    try {
      const found = await api.get(`/recipes/sources/collection${qs({ url: hit.url })}`);
      setExpansion(found);
      setShowing(true);
      const count = found.results?.length || 0;
      announce(count
        ? `${count} recipe${count === 1 ? '' : 's'} from this list`
        : nothingFound(found.reason));
    } catch (err) {
      announce(err.code === 'COLLECTION_UNREADABLE' ? refusedNote(hit.source_name) : err.message);
      throw err;
    }
  });

  const toggle = () => {
    // Fetched once. Hiding and showing again must not send us back to the
    // publisher for a page we are already holding, and the guard on `busy`
    // is what stops an impatient second press doing the same.
    if (expansion) { setShowing((shown) => !shown); return; }
    if (!expanding.busy) fetchChildren();
  };

  // A roundup is worth expanding and not worth importing: the recipes it names
  // are what was wanted from it. Opening and importing both stay available --
  // the label is a guess from a title, and the page itself decides.
  const isCollection = Boolean(hit.is_collection);
  const results = expansion?.results || [];
  // Only meaningful when nothing came back. A list that yielded recipes has
  // said everything it needs to say, in cards. An empty answer is given a
  // reason even when the server named none: the announcement is made either
  // way, and a card that says nothing while announcing something is the
  // disagreement all of this exists to prevent.
  const emptyReason = !expansion || results.length ? null : (expansion.reason || 'unknown');
  const refused = expanding.error?.code === 'COLLECTION_UNREADABLE';
  const untried = !expansion && !expanding.busy && !expanding.error;
  // Expanding has been tried and there is nothing to show for it. The sentence
  // saying so takes the button's place: what is left to press would fetch the
  // same page to be told the same thing, or hide a list that is not there.
  const nothingToShow = Boolean(emptyReason) || refused;

  // Which action the card leads with. Expanding, until expanding has been tried
  // and come back with nothing -- after which the honest lead is whatever the
  // outcome says the way in actually is.
  const lead = !canExpand ? 'open'
    : emptyReason === 'single-recipe' ? 'add'
      : nothingToShow ? 'open'
        : 'expand';

  const expandLabel = expanding.busy
    ? <><Spinner /> Finding the recipes</>
    : showing ? 'Hide the recipes' : 'Show the recipes';

  // Where the offer to expand lives on this card, if it lives anywhere. A hit
  // labelled a collection leads with the button; a hit nobody labelled one gets
  // the same offer inside the refusal, once the page has said it is a list
  // after all. Both at once would be two copies of one button.
  const leadsWithExpand = isCollection && lead === 'expand';
  const offerInRefusal = Boolean(collection?.expandable) && canExpand
    && !leadsWithExpand && !nothingToShow;

  const summary = hit.summary || null;
  // Short counts as "to buy" alongside missing: half the flour the recipe wants
  // is another trip to the shop, and burying it under "have it" would send
  // somebody to the kitchen to find out.
  const toBuy = summary ? (summary.missing || 0) + (summary.short || 0) : 0;
  const missing = hit.missing_items || [];
  const uncertain = hit.uncertain_items || [];
  const unchecked = hit.checked === false;
  const saved = Boolean(hit.already_saved && hit.recipe_id);

  return (
    <div className="card stack">
      <div className="row-between" style={{ gap: 8, alignItems: 'flex-start' }}>
        <div className="grow" style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 580 }}>{hit.title || hit.url}</div>
          <div className="tiny muted">{hit.source_name}</div>
          {/* This card was never a search result -- it came out of a roundup the
              search returned. Saying so is what keeps the list honest: otherwise
              a recipe appears that no query obviously asked for. */}
          {hit.from_collection && (
            <div className="tiny muted">From &ldquo;{hit.from_collection}&rdquo;</div>
          )}
        </div>
        <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
          {summary && (summary.can_make
            ? <Pill tone="ok">ready</Pill>
            : <Pill tone={toBuy ? 'warn' : 'unknown'}>
              {toBuy ? `${toBuy} to buy` : `${summary.unknown || 0} unclear`}
            </Pill>)}
          {!summary && unchecked && <Pill tone="unknown">not checked</Pill>}
          {isCollection && <Pill tone="default">collection</Pill>}
          {hit.import_blocked && <Pill tone="warn">manual step</Pill>}
        </div>
      </div>

      {hit.description && (
        <p className="tiny muted" style={{ margin: 0 }}>{hit.description}</p>
      )}

      {missing.length > 0 && (
        <div className="tiny muted">
          Need: {missing.map((item) => item.name).join(', ')}
        </div>
      )}
      {/* Kept separate from "need": an unverifiable ingredient might well be in
          the cupboard, and lumping it in would misstate the list. */}
      {uncertain.length > 0 && (
        <div className="tiny muted">
          Can&rsquo;t verify: {uncertain.map((item) => item.name).join(', ')}
        </div>
      )}

      {unchecked && UNCHECKED_REASON[hit.unchecked_reason] && (
        <div className="tiny muted">{UNCHECKED_REASON[hit.unchecked_reason]}</div>
      )}

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {saved ? (
          <>
            <Link
              className="button primary grow"
              to={`/recipes/${hit.recipe_id}`}
              style={{ textAlign: 'center' }}
            >
              Already saved &mdash; open it
            </Link>
            <a
              className="button"
              href={hit.url}
              target="_blank"
              rel="noreferrer noopener"
            >
              Open
            </a>
          </>
        ) : isCollection ? (
          <>
            {/* A line to itself. Three buttons abreast on a phone squeezed this
                one down to its shortest word and stacked the label three deep,
                which made the action the card leads with the hardest of the
                three to read. */}
            {lead === 'expand' && (
              <button
                type="button"
                className="primary full"
                disabled={expanding.busy}
                aria-expanded={showing}
                onClick={toggle}
              >
                {expandLabel}
              </button>
            )}
            <a
              className={lead === 'open' ? 'button primary grow' : 'button'}
              href={hit.url}
              target="_blank"
              rel="noreferrer noopener"
              style={lead === 'open' ? { textAlign: 'center' } : undefined}
            >
              Open the list
            </a>
            <button
              type="button"
              className={lead === 'add' ? 'primary grow' : undefined}
              disabled={busy}
              onClick={add}
            >
              {busy ? 'Adding…' : lead === 'add' ? 'Add to my recipes' : 'Add anyway'}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="primary grow" disabled={busy} onClick={add}>
              {busy ? 'Adding…' : 'Add to my recipes'}
            </button>
            <a
              className="button"
              href={hit.url}
              target="_blank"
              rel="noreferrer noopener"
            >
              Open
            </a>
          </>
        )}
      </div>

      {isCollection && !collection && untried && (
        <p className="tiny muted" style={{ margin: 0 }}>
          {canExpand
            ? 'Looks like several recipes in one article. Show them here and add the ones you want.'
            : 'Looks like several recipes in one article. Open it and import the one you want.'}
        </p>
      )}

      {duplicate && (
        <div className="alert">
          Already in your recipes. <Link to={`/recipes/${duplicate}`}>Open it</Link>
        </div>
      )}

      {blocked && (
        <div className="alert warn">
          {hit.source_name} blocks automatic imports. Open the page, copy the recipe,
          and use <strong>Paste a recipe</strong> on the Recipes screen &mdash; that path
          always works.
        </div>
      )}

      {/* The refusal, and the one way out of it. The page says what it is and
          this side says what to do about it, because only this side knows what
          it is offering: a card inside an expansion has no button to press, and
          a card already leading with one does not need telling twice. An
          instruction written anywhere else contradicts what is rendered here. */}
      {collection && (
        <div className="alert warn">
          <div>{collection.message}</div>
          {offerInRefusal && (
            <button
              type="button"
              className="small"
              style={{ marginTop: 8 }}
              disabled={expanding.busy}
              aria-expanded={showing}
              onClick={toggle}
            >
              {expandLabel}
            </button>
          )}
          {/* Nothing, when the button above the refusal is already the
              instruction. Otherwise the way in that is always true: a card
              inside an expansion cannot open the list up again, and a list that
              opened into nothing has nowhere else to send anybody. */}
          {!offerInRefusal && !leadsWithExpand && (
            <div style={{ marginTop: 8 }}>Open it and import the recipe you want.</div>
          )}
        </div>
      )}

      {!blocked && !collection && !duplicate && <ErrorNote error={error} />}

      {refused && (
        <p className="small muted" style={{ margin: 0 }}>{refusedNote(hit.source_name)}</p>
      )}
      {emptyReason && (
        <p className="small muted" style={{ margin: 0 }}>{nothingFound(emptyReason)}</p>
      )}
      {!refused && <ErrorNote error={expanding.error} />}

      {showing && results.length > 0 && (
        <div className="collection-children stack">
          {/* Said only when it is true: the cards account for themselves, but
              a list that was cut off does not look cut off. */}
          {expansion.truncated && (
            <p className="small muted" style={{ margin: 0 }}>
              The first {results.length} on the list. Open it for the rest.
            </p>
          )}
          {results.map((child) => (
            <SearchHit
              key={child.url}
              hit={child}
              onImported={onImported}
              canExpand={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}
